/**
 * ProfessionalDetailsPage — a ficha do profissional (#345).
 *
 * A listagem já oferecia "Visualizar" apontando para `/professionals/:id`, mas
 * a rota não existia: o catch-all do `App` mandava o usuário para a home sem
 * dizer nada. Esta é a tela que faltava.
 *
 * Segue a receita da skill `frontend-screens`, em quatro camadas:
 *   1. identidade  — `IdentityBanner` (o nome do registro vive só aqui)
 *   2. destaques   — `StatCard`
 *   3. temáticos   — `SectionCard` + `InfoItem`
 *   4. relacionados — `SectionCard` com `badge` de contagem
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AssignmentInd as AssignmentIndIcon,
  AccountBalance as AccountBalanceIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon,
  WorkspacePremium as RegistroIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  PageShell,
  IdentityBanner,
  IdentifierChip,
  InfoItem,
  SectionCard,
  StatCard,
} from '../../components';
import { pode } from '../../authorization/pode';
import { RouteActionButton } from '../../components/common';
import ProfessionalArtsSection from './components/ProfessionalArtsSection';
import { professionalService } from '../../services/professionalService';
import {
  Professional,
  ProfessionalType,
  PROFESSIONAL_TYPE_LABELS,
} from '../../types/professional';
import {
  STATUS_COLORS,
  formatCPF,
  getInitials,
  getProfessionalType,
  getRegistration,
  isMetaTruthy,
  REGISTRO_POR_CHAVE,
} from './professionalDisplay';

/** `createdAt`/`updatedAt` são timestamps completos — data local basta na ficha. */
const formatarDataHora = (valor?: string | Date | null): string => {
  if (!valor) return '—';
  const data = typeof valor === 'string' ? new Date(valor) : valor;
  return Number.isNaN(data.getTime()) ? '—' : data.toLocaleDateString('pt-BR');
};

/** Só dígitos, para montar o link do WhatsApp. */
const somenteDigitos = (valor: string): string => valor.replace(/\D/g, '');

const RESPONSABILIDADES: ReadonlyArray<{ chave: string; rotulo: string }> = [
  { chave: 'isExaminer', rotulo: 'Médico Examinador' },
  { chave: 'isCoordinator', rotulo: 'Coordenador' },
  { chave: 'isMedicalResponsible', rotulo: 'Responsável Médico' },
  { chave: 'isTechnicalResponsible', rotulo: 'Responsável Técnico' },
  { chave: 'isEnvironmentalResponsible', rotulo: 'Responsável Ambiental' },
  { chave: 'isLegalResponsible', rotulo: 'Responsável Legal' },
];

const ProfessionalDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [professional, setProfessional] = useState<Professional | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    if (!id) {
      setError('ID do profissional não fornecido.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setProfessional(await professionalService.getProfessionalById(id));
      setError(null);
    } catch (err: unknown) {
      const mensagem =
        err instanceof Error ? err.message : 'Não foi possível carregar o profissional';
      setError(mensagem);
      enqueueSnackbar(mensagem, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const breadcrumbs = [
    { label: 'Início', path: '/home' },
    { label: 'Profissionais', path: '/professionals' },
    { label: professional?.name ?? 'Detalhes' },
  ];

  /*
    O `Voltar` acompanha a tela em TODOS os estados — carregando, erro e
    conteúdo. Tela quebrada sem saída era o defeito do padrão antigo.
  */
  const botaoVoltar = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate('/professionals')}
    >
      Voltar
    </Button>
  );

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={botaoVoltar}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (error || !professional) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={botaoVoltar}>
        <Typography variant="body1" color="error">
          {error ?? 'Profissional não encontrado.'}
        </Typography>
      </PageShell>
    );
  }

  const { meta } = professional;
  const tipo = getProfessionalType(professional);
  const rotuloDoTipo = tipo
    ? (PROFESSIONAL_TYPE_LABELS[tipo as ProfessionalType] ?? tipo)
    : 'Tipo não informado';

  // `getRegistration` devolve '-' quando não há registro; na ficha o vazio é '—'.
  const registroBruto = getRegistration(professional);
  const registro = registroBruto === '-' ? '—' : registroBruto;

  const conselhos = REGISTRO_POR_CHAVE.filter((registro) => Boolean(meta?.[registro.chave]));
  const responsabilidades = RESPONSABILIDADES.filter((responsabilidade) =>
    isMetaTruthy((meta as Record<string, unknown> | undefined)?.[responsabilidade.chave])
  );

  const temDadosBancarios = Boolean(
    meta?.bankName ?? meta?.bankAgency ?? meta?.bankAccount ?? meta?.pixKey
  );

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      actions={
        <>
          {botaoVoltar}
          <RouteActionButton
            para="/professionals/:id/edit"
            exige={pode('professional.update')}
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            aoAgir={() => navigate(`/professionals/${professional.id}/edit`)}
          >
            Editar
          </RouteActionButton>
        </>
      }
    >
      {/* 1. Identidade */}
      <IdentityBanner
        avatar={getInitials(professional.name)}
        name={professional.name}
        status={
          <Chip
            size="small"
            label={professional.situacao}
            color={STATUS_COLORS[professional.situacao] ?? 'default'}
          />
        }
        context={rotuloDoTipo}
        identifiers={
          <>
            <IdentifierChip icon={<BadgeIcon />} label={formatCPF(professional.cpf)} />
            {professional.email && (
              <Tooltip title={professional.email}>
                <IconButton
                  size="small"
                  color="primary"
                  component="a"
                  href={`mailto:${professional.email}`}
                >
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {professional.whatsapp && (
              <Tooltip title={professional.whatsapp}>
                <IconButton
                  size="small"
                  color="success"
                  component="a"
                  target="_blank"
                  rel="noopener"
                  href={`https://wa.me/55${somenteDigitos(professional.whatsapp)}`}
                >
                  <WhatsAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />

      {/* 2. Destaques */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<RegistroIcon />}
            label="Registro Profissional"
            value={registro}
            sublabel={meta?.conselhoUf ? `UF ${meta.conselhoUf}` : undefined}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<AssignmentIndIcon />}
            label="Especialidade"
            value={meta?.specialty ?? '—'}
            sublabel={meta?.rqe ? `RQE ${meta.rqe}` : undefined}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<CalendarIcon />}
            label="Cadastrado em"
            value={formatarDataHora(professional.createdAt)}
            sublabel={`Atualizado em ${formatarDataHora(professional.updatedAt)}`}
            color={theme.palette.text.secondary}
          />
        </Grid>
      </Grid>

      {/* 3. Blocos temáticos */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Contato"
            icon={<PersonIcon />}
            accentColor={theme.palette.primary.main}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="CPF" value={formatCPF(professional.cpf)} icon={<BadgeIcon />} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem
                  label="WhatsApp"
                  value={professional.whatsapp}
                  icon={<WhatsAppIcon />}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InfoItem
                  label="E-mail"
                  value={professional.email}
                  icon={<EmailIcon />}
                  fullWidth
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Registro Profissional"
            icon={<RegistroIcon />}
            accentColor={theme.palette.info.main}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Tipo" value={tipo ? rotuloDoTipo : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="UF do Conselho" value={meta?.conselhoUf} />
              </Grid>
              {conselhos.map((registro) => (
                <Grid size={{ xs: 12, sm: 6 }} key={registro.chave}>
                  <InfoItem label={registro.rotulo} value={String(meta?.[registro.chave] ?? '')} />
                </Grid>
              ))}
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Especialidade" value={meta?.specialty} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="RQE" value={meta?.rqe} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Nº de Registro" value={meta?.registrationNumber} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Certificação" value={meta?.certification} />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Responsabilidades"
            icon={<AssignmentIndIcon />}
            accentColor={theme.palette.success.main}
            badge={<Chip size="small" label={responsabilidades.length} />}
          >
            {responsabilidades.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhuma responsabilidade atribuída
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {responsabilidades.map((responsabilidade) => (
                  <Chip
                    key={responsabilidade.chave}
                    size="small"
                    color="success"
                    variant="outlined"
                    label={responsabilidade.rotulo}
                  />
                ))}
              </Box>
            )}
          </SectionCard>
        </Grid>

        {temDadosBancarios && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Dados Bancários"
              icon={<AccountBalanceIcon />}
              accentColor={theme.palette.warning.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Banco" value={meta?.bankName} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Agência" value={meta?.bankAgency} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Conta" value={meta?.bankAccount} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Chave PIX" value={meta?.pixKey} />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        )}

        {meta?.observations && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Observações"
              icon={<NotesIcon />}
              accentColor={theme.palette.text.secondary}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {meta.observations}
              </Typography>
            </SectionCard>
          </Grid>
        )}
      </Grid>

      {/*
        4. Relacionados — o que existe vinculado a este profissional.

        Cada ação de ART persiste na hora, sem "Salvar" nenhum. Por isso o
        bloco vive AQUI, e não mais no formulário de edição: lá dividia a tela
        com um formulário que só grava no botão final, e quem excluía uma ART e
        clicava em "Cancelar" acreditava ter desfeito a exclusão.
      */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 4, mb: 2 }}>
        Responsabilidade Técnica
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ProfessionalArtsSection professionalId={professional.id} />
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default ProfessionalDetailsPage;
